import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { FAQ } from "@/components/site/article/FAQ";
import { DeskCallout } from "@/components/site/DeskCallout";
import { getTerminalCandles, type Candle } from "@/lib/terminal-candles.functions";

const SITE = "https://xauproacademy.com";
const PATH = "/gold-daily-range-atr";
const PAGE_URL = `${SITE}${PATH}`;
const TITLE = "Gold Daily Range and ATR Today: XAUUSD ADR | XAU Pro Academy";
const DESC =
  "Live XAUUSD average daily range (ADR 5, 14, 30) and 14 day ATR, how much of today's gold range is already used, session volatility profile and an ATR stop distance calculator.";

const FAQS = [
  { q: "What is the average daily range of gold?", a: "The average daily range (ADR) is the mean distance between the daily high and low over a set number of sessions. This page measures it live from completed XAUUSD trading days using the 22:00 UTC day boundary, over 5, 14 and 30 days." },
  { q: "What is the difference between ADR and ATR?", a: "ADR averages high minus low only. ATR (Average True Range) also includes overnight gaps by taking the largest of high minus low, high minus previous close, and previous close minus low. On gold the two are usually close, but ATR is larger after gap openings." },
  { q: "How do you use ATR to set a stop loss on gold?", a: "Multiply a timeframe ATR by a factor and place the stop beyond structure at least that distance away. The stop must still sit beyond the invalidation level. A volatility multiple is a minimum distance check, not a replacement for structure." },
  { q: "What does range used mean?", a: "It is today's high to low divided by the 14 day ADR. Above roughly 80% most of the typical move has already been delivered, so late continuation entries carry weaker reward relative to risk." },
  { q: "Which session delivers most of gold's daily range?", a: "London and the New York overlap usually deliver the majority. Asian hours typically build a narrower range that London later tests. The session profile on this page measures the recent shares directly from H1 bars." },
  { q: "Is a tight stop on gold a good idea?", a: "Rarely. Gold routinely moves tens of dollars per day. A stop that is a small fraction of hourly volatility is often removed by normal noise before the idea is tested. Size the position down instead of tightening the stop." },
];

const SECTIONS = [
  ["today", "Today"],
  ["averages", "ADR and ATR"],
  ["sessions", "Session profile"],
  ["calculator", "ATR stop calculator"],
  ["method", "Method"],
  ["faq", "FAQ"],
] as const;

export const Route = createFileRoute("/gold-daily-range-atr")({
  loader: async () => {
    const [d1, h1] = await Promise.all([
      getTerminalCandles({ data: { tf: "D1" } }).catch(() => null),
      getTerminalCandles({ data: { tf: "H1" } }).catch(() => null),
    ]);
    return { d1: d1?.candles ?? [], h1: h1?.candles ?? [] };
  },
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: "gold daily range, xauusd average daily range, gold adr, xauusd atr, gold atr today, gold atr calculator, gold volatility, gold stop loss atr" },
      { property: "og:title", content: "Gold Daily Range and ATR Today (XAUUSD)" },
      { property: "og:description", content: "Live XAUUSD ADR and ATR, range used today, session volatility shares and an ATR stop distance calculator." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Gold Daily Range and ATR Calculator (XAUUSD)",
          url: PAGE_URL,
          applicationCategory: "FinanceApplication",
          operatingSystem: "Any",
          description: DESC,
          featureList: ["Live XAUUSD ADR 5, 14 and 30 day", "14 day Average True Range", "Daily range used percentage", "Session volatility profile", "ATR stop distance and position size"],
          browserRequirements: "Requires JavaScript. Modern HTML5 browser",
          softwareVersion: "2026.1",
          isAccessibleForFree: true,
          inLanguage: "en",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/OnlineOnly" },
          publisher: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Gold Daily Range and ATR", item: PAGE_URL },
          ],
        }),
      },
    ],
  }),
  component: GoldRangePage,
});

const usd = (x: number | null, d = 2) =>
  x == null || !Number.isFinite(x) ? "n/a" : "$" + x.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

type Stats = {
  today: Candle | null;
  todayRange: number | null;
  adr5: number | null;
  adr14: number | null;
  adr30: number | null;
  atr14: number | null;
  used: number | null;
  last10: { t: number; range: number }[];
};

function computeStats(d1: Candle[]): Stats {
  if (d1.length < 3) return { today: null, todayRange: null, adr5: null, adr14: null, adr30: null, atr14: null, used: null, last10: [] };
  const today = d1[d1.length - 1];
  const done = d1.slice(0, -1);
  const ranges = done.map((c) => c.h - c.l);
  const tr = done.slice(1).map((c, i) => Math.max(c.h - c.l, Math.abs(c.h - done[i].c), Math.abs(done[i].c - c.l)));
  const adr14 = avg(ranges.slice(-14));
  const todayRange = today.h - today.l;
  return {
    today,
    todayRange,
    adr5: avg(ranges.slice(-5)),
    adr14,
    adr30: avg(ranges.slice(-30)),
    atr14: avg(tr.slice(-14)),
    used: adr14 ? todayRange / adr14 : null,
    last10: done.slice(-10).map((c) => ({ t: c.t, range: c.h - c.l })),
  };
}

// UTC hour windows. Day boundary 22:00 UTC.
const SESSIONS = [
  { id: "asia", label: "Asia", from: 22, to: 7 },
  { id: "london", label: "London", from: 7, to: 12 },
  { id: "ny", label: "New York overlap", from: 12, to: 17 },
  { id: "late", label: "Late New York", from: 17, to: 22 },
] as const;

function inSession(hour: number, s: (typeof SESSIONS)[number]) {
  return s.from < s.to ? hour >= s.from && hour < s.to : hour >= s.from || hour < s.to;
}

function sessionProfile(h1: Candle[]) {
  const days = new Map<number, Candle[]>();
  for (const c of h1) {
    const k = Math.floor((c.t + 7200) / 86400);
    const arr = days.get(k) ?? [];
    arr.push(c);
    days.set(k, arr);
  }
  const keys = [...days.keys()].sort((a, b) => a - b).slice(0, -1); // completed days only
  const shares: Record<string, number[]> = { asia: [], london: [], ny: [], late: [] };
  let used = 0;
  for (const k of keys) {
    const bars = days.get(k)!;
    if (bars.length < 18) continue;
    const dayR = Math.max(...bars.map((b) => b.h)) - Math.min(...bars.map((b) => b.l));
    if (dayR <= 0) continue;
    used++;
    for (const s of SESSIONS) {
      const sb = bars.filter((b) => inSession(new Date(b.t * 1000).getUTCHours(), s));
      if (!sb.length) continue;
      shares[s.id].push((Math.max(...sb.map((b) => b.h)) - Math.min(...sb.map((b) => b.l))) / dayR);
    }
  }
  return { days: used, rows: SESSIONS.map((s) => ({ ...s, share: avg(shares[s.id]) })) };
}

function regime(used: number | null) {
  if (used == null) return { label: "No reading", note: "Insufficient data for a range reading." };
  if (used < 0.5) return { label: "Building", note: "Less than half the typical range is delivered. Expansion potential remains, provided structure confirms." };
  if (used < 0.8) return { label: "Expanding", note: "The day is delivering a normal range. Continuation is possible but the remaining distance is narrowing." };
  if (used < 1.1) return { label: "Mature", note: "Most of the typical range is used. Late continuation entries carry weaker reward relative to risk." };
  return { label: "Extended", note: "Today exceeds its 14 day average range. Expect slower follow through and higher reversal risk near liquidity." };
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-md border border-border bg-background p-4">
      <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono text-xl font-semibold text-foreground">{value}</div>
      {sub ? <div className="mt-1 text-xs text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

function Field({ label, value, onChange, step, min }: { label: string; value: number; onChange: (v: number) => void; step: number; min?: number }) {
  return (
    <label className="block">
      <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        value={Number.isFinite(value) ? value : ""}
        step={step}
        min={min}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm text-foreground focus:border-gold focus:outline-none"
      />
    </label>
  );
}

function UsedMeter({ used }: { used: number | null }) {
  const pct = used == null ? 0 : Math.min(used, 1.5) / 1.5;
  const x = 20 + pct * 520;
  return (
    <svg viewBox="0 0 560 84" role="img" aria-labelledby="um-t um-d" className="w-full">
      <title id="um-t">Daily range used</title>
      <desc id="um-d">{`Today's gold range is ${used == null ? "unknown" : Math.round(used * 100) + "%"} of its 14 day average daily range. Zones mark building, expanding, mature and extended conditions.`}</desc>
      {[
        [0, 0.5, "BUILDING"],
        [0.5, 0.8, "EXPANDING"],
        [0.8, 1.1, "MATURE"],
        [1.1, 1.5, "EXTENDED"],
      ].map(([a, b, l], i) => (
        <g key={String(l)}>
          <rect x={20 + ((a as number) / 1.5) * 520} y={30} width={(((b as number) - (a as number)) / 1.5) * 520 - 2} height={18} rx={2} className="fill-gold" style={{ opacity: 0.12 + i * 0.12 }} />
          <text x={20 + ((a as number) / 1.5) * 520 + 4} y={64} className="fill-muted-foreground" style={{ font: "10px monospace" }}>{l as string}</text>
        </g>
      ))}
      {used != null ? (
        <g>
          <line x1={x} x2={x} y1={22} y2={54} className="stroke-foreground" strokeWidth={2} />
          <text x={Math.min(Math.max(x, 40), 520)} y={16} textAnchor="middle" className="fill-foreground" style={{ font: "600 12px monospace" }}>{`${Math.round(used * 100)}% OF ADR 14`}</text>
        </g>
      ) : null}
    </svg>
  );
}

function RangeBars({ rows, adr }: { rows: { t: number; range: number }[]; adr: number | null }) {
  if (!rows.length) return null;
  const max = Math.max(...rows.map((r) => r.range), adr ?? 0) || 1;
  const bw = 520 / rows.length;
  const yA = adr ? 150 - (adr / max) * 120 : null;
  return (
    <figure className="rounded-lg border border-border bg-card p-5">
      <svg viewBox="0 0 560 180" role="img" aria-labelledby="rb-t rb-d" className="w-full">
        <title id="rb-t">XAUUSD daily ranges, last 10 completed sessions</title>
        <desc id="rb-d">Bars show each day's high to low range in dollars. The dashed line marks the 14 day average daily range.</desc>
        {rows.map((r, i) => {
          const h = (r.range / max) * 120;
          const d = new Date(r.t * 1000 + 7200 * 1000);
          return (
            <g key={r.t}>
              <rect x={20 + i * bw + 4} y={150 - h} width={bw - 8} height={h} rx={2} className="fill-gold" style={{ opacity: adr && r.range > adr ? 0.9 : 0.45 }} />
              <text x={20 + i * bw + bw / 2} y={168} textAnchor="middle" className="fill-muted-foreground" style={{ font: "9px monospace" }}>{`${d.getUTCMonth() + 1}/${d.getUTCDate()}`}</text>
              <text x={20 + i * bw + bw / 2} y={146 - h} textAnchor="middle" className="fill-foreground" style={{ font: "9px monospace" }}>{Math.round(r.range)}</text>
            </g>
          );
        })}
        {yA != null ? (
          <g>
            <line x1={20} x2={540} y1={yA} y2={yA} className="stroke-foreground" strokeDasharray="4 4" strokeWidth={1} />
            <text x={540} y={yA - 4} textAnchor="end" className="fill-foreground" style={{ font: "10px monospace" }}>{`ADR 14 ${usd(adr, 0)}`}</text>
          </g>
        ) : null}
      </svg>
    </figure>
  );
}

function AtrCalculator({ atr }: { atr: number | null }) {
  const [base, setBase] = useState(atr ? Math.round(atr * 100) / 100 : 40);
  const [mult, setMult] = useState(0.5);
  const [balance, setBalance] = useState(10000);
  const [riskPct, setRiskPct] = useState(1);
  const [contract, setContract] = useState(100);
  const r = useMemo(() => {
    const stop = (base || 0) * (mult || 0);
    const riskUsd = (balance || 0) * ((riskPct || 0) / 100);
    const lots = stop > 0 && contract > 0 ? riskUsd / (stop * contract) : null;
    return { stop, riskUsd, lots, tp1: stop * 2, tp2: stop * 3.5 };
  }, [base, mult, balance, riskPct, contract]);
  return (
    <div className="grid gap-6 rounded-lg border border-border bg-card p-5 lg:grid-cols-2">
      <div className="space-y-4">
        <div>
          <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">ATR multiple</span>
          <div className="mt-1 flex flex-wrap gap-2" role="group" aria-label="ATR multiple">
            {[0.25, 0.5, 0.75, 1].map((m) => (
              <Button key={m} size="sm" variant={mult === m ? "gold" : "outline"} aria-pressed={mult === m} onClick={() => setMult(m)}>{`${m}x`}</Button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="ATR or ADR ($ per oz)" value={base} onChange={setBase} step={0.5} min={0} />
          <Field label="Custom multiple" value={mult} onChange={setMult} step={0.05} min={0} />
          <Field label="Account balance ($)" value={balance} onChange={setBalance} step={100} min={0} />
          <Field label="Risk per trade (%)" value={riskPct} onChange={setRiskPct} step={0.1} min={0} />
          <Field label="Oz per lot" value={contract} onChange={setContract} step={1} min={0} />
        </div>
        <p className="text-xs text-muted-foreground">Seeded with the live 14 day daily ATR. Daily ATR multiples around 0.4x to 0.6x approximate an intraday structural stop on gold.</p>
      </div>
      <div className="grid content-start gap-3 sm:grid-cols-2">
        <Stat label="Stop distance" value={usd(r.stop)} sub="per ounce" />
        <Stat label="Risk budget" value={usd(r.riskUsd)} />
        <Stat label="Position size" value={r.lots == null ? "n/a" : `${r.lots.toFixed(2)} lots`} sub="risk / (stop x oz per lot)" />
        <Stat label="Targets at 2R / 3.5R" value={`${usd(r.tp1, 0)} / ${usd(r.tp2, 0)}`} sub="distance from entry" />
        <p className="text-xs text-muted-foreground sm:col-span-2">The stop must still sit beyond the structural invalidation level. If structure requires a wider stop than the volatility minimum, use the wider one and reduce size.</p>
      </div>
    </div>
  );
}

function GoldRangePage() {
  const { d1, h1 } = Route.useLoaderData();
  const s = useMemo(() => computeStats(d1), [d1]);
  const prof = useMemo(() => sessionProfile(h1), [h1]);
  const reg = regime(s.used);

  return (
    <Layout>
      <article className="mx-auto max-w-5xl px-4 py-12">
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Market Tools / XAUUSD</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight md:text-5xl">Gold Daily Range and ATR Today</h1>
        <p className="mt-5 max-w-3xl text-lg leading-relaxed text-muted-foreground">
          Gold's 14 day average daily range is currently <strong className="text-foreground">{usd(s.adr14)}</strong> and the 14 day ATR is <strong className="text-foreground">{usd(s.atr14)}</strong>. Use these readings to judge how much of today's move is already delivered and to keep stop distances consistent with real volatility.
        </p>

        <nav aria-label="On this page" className="sticky top-16 z-20 -mx-4 mt-8 border-y border-border bg-background/95 px-4 py-2 backdrop-blur">
          <ul className="flex gap-4 overflow-x-auto font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            {SECTIONS.map(([id, label]) => (
              <li key={id}><a href={`#${id}`} className="whitespace-nowrap hover:text-gold">{label}</a></li>
            ))}
          </ul>
        </nav>

        <section id="today" className="scroll-mt-32 pt-10">
          <h2 className="text-2xl font-semibold">Today's range reading</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Today high" value={usd(s.today?.h ?? null)} />
            <Stat label="Today low" value={usd(s.today?.l ?? null)} />
            <Stat label="Today range" value={usd(s.todayRange)} />
            <Stat label="Range used" value={s.used == null ? "n/a" : `${Math.round(s.used * 100)}%`} sub="of ADR 14" />
          </div>
          <div className="mt-5 rounded-lg border border-border bg-card p-5">
            <UsedMeter used={s.used} />
            <p className="mt-3 text-sm text-muted-foreground"><span className="font-mono text-xs uppercase tracking-wider text-gold">{reg.label}.</span> {reg.note}</p>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Trading day boundary 22:00 UTC. Values refresh with each page load and exclude the current incomplete day from averages.</p>
        </section>

        <section id="averages" className="scroll-mt-32 pt-14">
          <h2 className="text-2xl font-semibold">Average daily range and ATR</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="ADR 5" value={usd(s.adr5)} sub="last week" />
            <Stat label="ADR 14" value={usd(s.adr14)} sub="reference" />
            <Stat label="ADR 30" value={usd(s.adr30)} sub="about six weeks" />
            <Stat label="ATR 14 (daily)" value={usd(s.atr14)} sub="includes gaps" />
          </div>
          <p className="mt-4 mb-5 text-sm text-muted-foreground">
            {s.adr5 != null && s.adr30 != null
              ? s.adr5 > s.adr30 * 1.15
                ? "Short term volatility is above the six week norm. Stops and position sizes should reflect the wider range."
                : s.adr5 < s.adr30 * 0.85
                  ? "Short term volatility is compressed below the six week norm. Compression often precedes expansion, typically around macro releases."
                  : "Short term volatility is in line with the six week norm."
              : "Insufficient history for a volatility comparison."}
          </p>
          <RangeBars rows={s.last10} adr={s.adr14} />
        </section>

        <section id="sessions" className="scroll-mt-32 pt-14">
          <h2 className="text-2xl font-semibold">Session volatility profile</h2>
          <p className="mt-2 mb-5 text-muted-foreground">Average share of the daily range covered by each session's own high to low, measured over the last {prof.days} completed trading days. Shares overlap and do not sum to 100%.</p>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-card font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr><th className="p-3 text-left">Session</th><th className="p-3 text-left">UTC</th><th className="p-3 text-right">Share of daily range</th><th className="p-3 text-left">Approx. dollars at ADR 14</th></tr>
              </thead>
              <tbody>
                {prof.rows.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="p-3 font-semibold">{r.label}</td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{`${String(r.from).padStart(2, "0")}:00 to ${String(r.to).padStart(2, "0")}:00`}</td>
                    <td className="p-3 text-right font-mono">{r.share == null ? "n/a" : `${Math.round(r.share * 100)}%`}</td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{r.share != null && s.adr14 ? usd(r.share * s.adr14, 0) : "n/a"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            See the <Link to="/gold-trading-sessions" className="text-gold hover:underline">live session clock</Link> for current session status and news freeze windows.
          </p>
        </section>

        <section id="calculator" className="scroll-mt-32 pt-14">
          <h2 className="text-2xl font-semibold">ATR stop distance calculator</h2>
          <p className="mt-2 mb-5 text-muted-foreground">Translate volatility into a minimum stop distance, then size the position from your risk budget.</p>
          <AtrCalculator atr={s.atr14} />
        </section>

        <section id="method" className="scroll-mt-32 pt-14">
          <h2 className="text-2xl font-semibold">Method</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {[
              ["Daily range", "High - Low of one trading day", "Day boundary 22:00 UTC"],
              ["ADR n", "Mean daily range of the last n completed days", "n = 5, 14, 30"],
              ["True range", "max(H - L, |H - prev C|, |prev C - L|)", "ATR 14 = mean of last 14"],
            ].map(([t, f, e]) => (
              <div key={t} className="rounded-lg border border-border bg-card p-4">
                <div className="font-mono text-[11px] uppercase tracking-wider text-gold">{t}</div>
                <div className="mt-2 font-mono text-sm text-foreground">{f}</div>
                <div className="mt-2 font-mono text-xs text-muted-foreground">{e}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 space-y-4 leading-relaxed text-muted-foreground">
            <p>Range readings describe volatility, not direction. A mature or extended day is not a reversal signal. It means the remaining distance to the typical extreme is short, so new entries need a stronger structural reason and a realistic target.</p>
            <p>Volatility also defines the minimum sensible stop. Gold stops sized like currency pair stops are usually removed by routine noise. When volatility rises, reduce position size rather than tightening the stop.</p>
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button asChild variant="gold"><Link to="/position-size-calculator">Size the position from risk <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
            <Button asChild variant="outline"><Link to="/xauusd-analysis-today">Gold analysis today</Link></Button>
          </div>
        </section>

        <section id="faq" className="scroll-mt-32 pt-14">
          <FAQ items={FAQS} />
        </section>

        <DeskCallout
          className="mt-14"
          eyebrow="Volatility sets the floor. Structure sets the stop."
          title="Every desk trade uses a fixed structural stop sized for gold's real range."
          body="OTAS-01 publishes entry, stop and targets before price moves, and every closed trade is logged with its R multiple in the audited archive."
        />

        <aside className="mt-6 rounded-lg border border-border bg-card p-6">
          <h2 className="text-lg font-semibold">Related references</h2>
          <ul className="mt-3 grid gap-2 text-sm md:grid-cols-2">
            <li><Link to="/xauusd-volatility-heatmap" className="text-gold hover:underline">XAUUSD volatility heatmap by hour</Link></li>
            <li><Link to="/gold-pip-calculator" className="text-gold hover:underline">Gold pip value calculator</Link></li>
            <li><Link to="/gold-margin-calculator" className="text-gold hover:underline">Gold margin calculator</Link></li>
            <li><Link to="/gold-trading-sessions" className="text-gold hover:underline">Gold trading hours clock</Link></li>
            <li><Link to="/gold-trading-strategy" className="text-gold hover:underline">Gold trading strategy playbook</Link></li>
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">Educational tool. Readings derive from XAUUSD spot bars and may differ slightly from your broker's feed. Trading leveraged products carries substantial risk.</p>
        </aside>
      </article>
    </Layout>
  );
}
